import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Search, Loader2, Trash2, Save, Filter } from "lucide-react";
import { toast } from "sonner";
import {
  listFeedback,
  updateFeedback,
  deleteFeedback,
  listFeatureRequests,
  updateFeatureRequest,
  reclusterFeedback,
} from "@/lib/api";

const TOKEN_KEY = "roadrig.admin.token";

const FEEDBACK_STATUSES = ["New", "Under Review", "Implemented", "Future Version"];
const FEATURE_STATUSES = ["New", "Under Review", "Planned", "In Development", "Released"];
const PRODUCTS = ["", "OUTLAW", "GHOST", "EXECUTIVE", "TACTICAL", "TITAN", "Future Ideas"];

export default function AdminFeedback() {
  useEffect(() => { document.title = "ROADRIG · Admin · Feedback"; }, []);

  const [token, setToken] = useState("");
  const [tab, setTab] = useState("wall"); // wall | features
  const [feedback, setFeedback] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reclustering, setReclustering] = useState(false);
  const [filters, setFilters] = useState({ q: "", product: "", status: "" });

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY) || "";
    setToken(t);
  }, []);

  const saveToken = (val) => {
    setToken(val);
    if (val) localStorage.setItem(TOKEN_KEY, val);
    else localStorage.removeItem(TOKEN_KEY);
  };

  const loadAll = async () => {
    if (!token) { toast.error("Paste your admin token first."); return; }
    setLoading(true);
    try {
      const [fb, fr] = await Promise.all([
        listFeedback(token, filters),
        listFeatureRequests(token),
      ]);
      setFeedback(fb || []);
      setFeatures(fr || []);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid admin token." : "Load failed.");
    } finally {
      setLoading(false);
    }
  };

  const recluster = async () => {
    if (!token) return toast.error("Paste your admin token first.");
    setReclustering(true);
    try {
      const res = await reclusterFeedback(token);
      if (res?.ok) {
        toast.success(`Reclustered into ${res.features} feature request(s).`);
        loadAll();
      } else {
        toast.error(res?.reason || "Recluster failed.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recluster failed.");
    } finally {
      setReclustering(false);
    }
  };

  const stats = useMemo(() => {
    const total = feedback.length;
    const byProduct = {};
    const byStatus = {};
    feedback.forEach((f) => {
      byProduct[f.product_interest || "—"] = (byProduct[f.product_interest || "—"] || 0) + 1;
      byStatus[f.status || "New"] = (byStatus[f.status || "New"] || 0) + 1;
    });
    return { total, byProduct, byStatus, features: features.length };
  }, [feedback, features]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="admin-feedback-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <Link to="/" className="inline-flex items-center gap-2 serial text-[10px] text-white/40 hover:text-white transition-colors mb-8">
          <ArrowLeft size={12} /> BACK TO HOME
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="overline text-[#F47A20] mb-2">Admin · Rider Feedback</div>
            <h1 className="h-display text-white text-3xl md:text-4xl">Idea Wall & Feature Requests</h1>
            <p className="text-white/50 text-sm mt-2">Auto-grouped by Claude Sonnet 4.6. Every submission is read.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={recluster}
              disabled={!token || reclustering}
              data-testid="admin-recluster-btn"
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {reclustering ? <><Loader2 size={14} className="animate-spin" /> Reclustering…</> : <><RefreshCw size={14} /> Force recluster</>}
            </button>
            <button
              onClick={loadAll}
              disabled={!token || loading}
              data-testid="admin-load-btn"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <><Loader2 size={14} className="animate-spin" /> Loading…</> : <>Load</>}
            </button>
          </div>
        </div>

        {/* Admin token */}
        <section className="border border-white/10 rounded-sm p-5 mb-8">
          <div className="overline text-white/60 mb-2 inline-block">Admin token</div>
          <input
            type="password"
            data-testid="admin-token-input"
            placeholder="Paste JOURNAL_ADMIN_TOKEN…"
            value={token}
            onChange={(e) => saveToken(e.target.value)}
            className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-4 py-3 rounded-sm transition-colors"
          />
          <p className="text-white/40 text-xs mt-2">Stored locally in your browser. Sent only as X-Admin-Token.</p>
        </section>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-feedback-stats">
          <Stat label="Submissions" value={stats.total} />
          <Stat label="Feature clusters" value={stats.features} />
          <Stat label="New" value={stats.byStatus.New || 0} />
          <Stat label="Implemented" value={stats.byStatus.Implemented || 0} />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-6" data-testid="admin-feedback-tabs">
          <TabBtn active={tab === "wall"} onClick={() => setTab("wall")} testid="tab-wall">
            Idea Wall ({feedback.length})
          </TabBtn>
          <TabBtn active={tab === "features"} onClick={() => setTab("features")} testid="tab-features">
            Feature Requests ({features.length})
          </TabBtn>
        </div>

        {tab === "wall" ? (
          <WallTab
            token={token}
            feedback={feedback}
            filters={filters}
            setFilters={setFilters}
            onReload={loadAll}
          />
        ) : (
          <FeaturesTab token={token} features={features} onReload={loadAll} />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 rounded-sm px-4 py-3">
      <div className="serial text-[10px] text-white/40">{label}</div>
      <div className="text-white text-2xl font-bold tracking-tight mt-1">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={
        "px-5 py-3 -mb-px text-sm font-semibold tracking-wide transition-colors " +
        (active
          ? "text-[#F47A20] border-b-2 border-[#F47A20]"
          : "text-white/50 hover:text-white border-b-2 border-transparent")
      }
    >
      {children}
    </button>
  );
}

/* ────────── Idea Wall ────────── */

function WallTab({ token, feedback, filters, setFilters, onReload }) {
  return (
    <div className="space-y-4" data-testid="admin-wall">
      <div className="flex flex-col md:flex-row md:items-end gap-3 border border-white/10 rounded-sm p-4">
        <div className="flex-1 min-w-0">
          <div className="overline text-white/60 mb-2 inline-block flex items-center gap-2"><Search size={11} /> Search</div>
          <input
            type="text"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="name, email, keywords…"
            data-testid="admin-search-input"
            className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-3 py-2 rounded-sm text-sm"
          />
        </div>
        <div>
          <div className="overline text-white/60 mb-2 inline-block flex items-center gap-2"><Filter size={11} /> Product</div>
          <select
            value={filters.product}
            onChange={(e) => setFilters((f) => ({ ...f, product: e.target.value }))}
            data-testid="admin-product-filter"
            className="bg-[#0A0A0C] border border-white/15 focus:border-[#F47A20] outline-none text-white px-3 py-2 rounded-sm text-sm"
          >
            {PRODUCTS.map((p) => <option key={p || "all"} value={p}>{p || "All products"}</option>)}
          </select>
        </div>
        <div>
          <div className="overline text-white/60 mb-2 inline-block">Status</div>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            data-testid="admin-status-filter"
            className="bg-[#0A0A0C] border border-white/15 focus:border-[#F47A20] outline-none text-white px-3 py-2 rounded-sm text-sm"
          >
            <option value="">All statuses</option>
            {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={onReload} className="btn-secondary" data-testid="admin-apply-filters">Apply</button>
      </div>

      {feedback.length === 0 ? (
        <p className="text-white/40 text-sm py-16 text-center border border-dashed border-white/10 rounded-sm">
          No feedback yet. Once riders submit, ideas appear here.
        </p>
      ) : (
        <div className="grid gap-3">
          {feedback.map((f) => <FeedbackCard key={f.id} f={f} token={token} onChange={onReload} />)}
        </div>
      )}
    </div>
  );
}

function FeedbackCard({ f, token, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(f.status || "New");
  const [notes, setNotes] = useState(f.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateFeedback(token, f.id, { status, notes });
      toast.success("Saved.");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try {
      await deleteFeedback(token, f.id);
      toast.success("Deleted.");
      onChange();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="border border-white/10 rounded-sm p-4 md:p-5" data-testid={`feedback-card-${f.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-white font-semibold text-sm">{f.name || "Anonymous"}</span>
            {f.email && <span className="text-white/40 text-xs">· {f.email}</span>}
            {f.product_interest && (
              <span className="serial text-[9px] px-2 py-0.5 rounded-sm bg-[#F47A20]/15 text-[#F47A20] border border-[#F47A20]/30">
                {f.product_interest}
              </span>
            )}
          </div>
          <div className="text-white/40 text-xs flex flex-wrap gap-2">
            {f.ride_what && <span>🏍 {f.ride_what}</span>}
            {f.riding_style && <span>· {f.riding_style}</span>}
            {f.riding_experience && <span>· {f.riding_experience}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid={`feedback-status-${f.id}`}
            className="bg-[#0A0A0C] border border-white/15 text-white text-xs px-2 py-1 rounded-sm"
          >
            {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            onClick={remove}
            data-testid={`feedback-delete-${f.id}`}
            className="text-white/40 hover:text-red-400 transition-colors p-1"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        {f.love_to_see && <FieldRow label="Wants" text={f.love_to_see} />}
        {f.biggest_problem && <FieldRow label="Problem" text={f.biggest_problem} />}
        {expanded && f.other_ideas && <FieldRow label="Other" text={f.other_ideas} />}
      </div>

      {(f.other_ideas || notes.length > 0 || status !== (f.status || "New")) && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[#F47A20] hover:underline text-xs mt-3"
          data-testid={`feedback-expand-${f.id}`}
        >
          {expanded ? "Collapse" : "Details"}
        </button>
      )}

      {expanded && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="overline text-white/60 mb-2 inline-block">Internal notes</div>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Only visible to admins"
            data-testid={`feedback-notes-${f.id}`}
            className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white text-sm placeholder:text-white/30 px-3 py-2 rounded-sm resize-y"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={save}
              disabled={saving}
              data-testid={`feedback-save-${f.id}`}
              className="btn-primary text-xs disabled:opacity-50"
            >
              {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : <><Save size={12} /> Save</>}
            </button>
          </div>
        </div>
      )}

      <div className="serial text-[9px] text-white/30 mt-3">
        {new Date(f.created_at).toLocaleString()} · {f.cluster_status}
      </div>
    </div>
  );
}

function FieldRow({ label, text }) {
  return (
    <div className="flex gap-2">
      <span className="serial text-[9px] text-white/40 uppercase pt-0.5 shrink-0 min-w-[54px]">{label}</span>
      <p className="text-white/80 whitespace-pre-wrap flex-1">{text}</p>
    </div>
  );
}

/* ────────── Feature Requests ────────── */

function FeaturesTab({ token, features, onReload }) {
  if (features.length === 0) {
    return (
      <p className="text-white/40 text-sm py-16 text-center border border-dashed border-white/10 rounded-sm" data-testid="admin-features-empty">
        No feature requests yet. Once feedback comes in, Claude will auto-group them here.
      </p>
    );
  }

  return (
    <div className="border border-white/10 rounded-sm overflow-hidden" data-testid="admin-features-table">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] border-b border-white/10">
          <tr>
            <th className="text-left px-4 py-3 serial text-[9px] text-white/50 uppercase tracking-wider">Feature</th>
            <th className="text-right px-4 py-3 serial text-[9px] text-white/50 uppercase tracking-wider w-24"># Riders</th>
            <th className="text-left px-4 py-3 serial text-[9px] text-white/50 uppercase tracking-wider w-48">Status</th>
            <th className="text-left px-4 py-3 serial text-[9px] text-white/50 uppercase tracking-wider w-36">Last updated</th>
          </tr>
        </thead>
        <tbody>
          {features.map((fr) => (
            <FeatureRow key={fr.id} fr={fr} token={token} onReload={onReload} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeatureRow({ fr, token, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(fr.status || "New");
  const [notes, setNotes] = useState(fr.internal_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateFeatureRequest(token, fr.id, { status, internal_notes: notes });
      toast.success("Saved.");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr
        className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        data-testid={`feature-row-${fr.id}`}
      >
        <td className="px-4 py-3 text-white font-medium">{fr.name}</td>
        <td className="px-4 py-3 text-right">
          <span className="inline-block min-w-[32px] text-center bg-[#F47A20]/15 text-[#F47A20] px-2 py-0.5 rounded-sm text-xs font-bold border border-[#F47A20]/30">
            {fr.count}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="serial text-[10px] text-white/70">{fr.status}</span>
        </td>
        <td className="px-4 py-3 serial text-[10px] text-white/40">
          {new Date(fr.last_updated).toLocaleDateString()}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/5 bg-white/[0.02]">
          <td colSpan={4} className="px-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="overline text-white/60 mb-2 inline-block">Description</div>
                <p className="text-white/80 text-sm mb-4 leading-relaxed">{fr.description || "—"}</p>

                <div className="overline text-white/60 mb-2 inline-block">Keywords</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(fr.keywords || []).map((k, i) => (
                    <span key={i} className="serial text-[10px] px-2 py-0.5 border border-white/15 rounded-sm text-white/60">{k}</span>
                  ))}
                </div>

                <div className="overline text-white/60 mb-2 inline-block">Submission IDs</div>
                <div className="serial text-[9px] text-white/40 leading-relaxed break-all">
                  {(fr.submission_ids || []).join(", ") || "—"}
                </div>
              </div>
              <div>
                <div className="overline text-white/60 mb-2 inline-block">Status</div>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid={`feature-status-${fr.id}`}
                  className="w-full bg-[#0A0A0C] border border-white/15 focus:border-[#F47A20] outline-none text-white text-sm px-3 py-2 rounded-sm mb-3"
                >
                  {FEATURE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <div className="overline text-white/60 mb-2 inline-block">Internal notes</div>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Design notes, priority, blockers…"
                  data-testid={`feature-notes-${fr.id}`}
                  className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white text-sm placeholder:text-white/30 px-3 py-2 rounded-sm resize-y mb-3"
                />
                <div className="flex justify-end">
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid={`feature-save-${fr.id}`}
                    className="btn-primary text-xs disabled:opacity-50"
                  >
                    {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : <><Save size={12} /> Save</>}
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
